import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { toDisplayInstant } from '../datetime/ist';
import { ApiClientService } from './api-client.service';
import { ComplaintService } from './complaint.service';

export type WorkflowAction =
  | 'Approve'
  | 'Correct'
  | 'Resend'
  | 'Assign Department'
  | 'Assign Officer'
  | 'Escalate'
  | 'Update Status'
  | 'Close'
  | 'Reopen'
  | 'Submit Report'
  | 'Need Hearing'
  | 'Mark Heard'
  | 'Resolve'
  | 'Reject';

export interface WorkflowItem {
  id: string | number;
  trackingId: string | number;
  subject: string;
  department: string;
  stage: string;
  priority: 'normal' | 'high' | 'urgent' | string;
  awaitingSince: string;
  allowed: WorkflowAction[];
}

export interface CaseRecord {
  id: string | number;
  caseNo: string | number;
  trackingId: string | number;
  subject: string;
  officer: string;
  assignedOfficerRef?: string | number | null;
  department: string;
  status: 'Open' | 'In Progress' | 'On Hold' | 'Closed';
  openedAt: string;
  notes: string;
  investigationDueAt?: string | null;
}

export interface HearingRecord {
  id: string | number;
  caseNo: string | number;
  subject: string;
  scheduledAt: string;
  hearingDate?: string;
  hearingTime?: string;
  venue: string;
  officers: string | number;
  officerName?: string;
  status: 'Scheduled' | 'Completed' | 'Rescheduled' | 'Cancelled';
}

export interface RosterOfficer {
  id: string | number;
  officerRef: string | number;
  name: string;
  role: string;
  department: string;
  availability: 'Available' | 'On Leave' | 'In Hearing' | 'Overloaded';
  workload: number;
  leaveUntil?: string;
  currentAssignment?: string | null;
}

export interface ResolutionRecord {
  id: string | number;
  caseNo: string | number;
  trackingId: string | number;
  decision: string;
  outcome: 'Granted' | 'Partially Granted' | 'Rejected' | 'Deferred';
  decidedBy: string;
  decidedAt: string;
  followUps: string;
}

export interface AppNotification {
  id: string | number;
  title: string;
  body: string;
  type: 'Assignment' | 'Hearing' | 'Workflow' | 'Resolution' | 'System';
  at: string;
  read: boolean;
  channel?: string;
  grievanceRef?: string | number;
  targetRole?: string | null;
  recipientRef?: string | number | null;
  nextStep?: string | null;
  actionPath?: string | null;
  actionLabel?: string | null;
}

export interface AdminUser {
  id: string | number;
  name: string;
  email: string;
  phone: string;
  role: string;
  roles?: string[];
  department: string;
  status: 'Active' | 'Suspended';
}

export interface PendingResolution {
  id: string | number;
  caseNo: string | number;
  trackingId: string | number;
  subject: string;
  /** Desk role the case was assigned to (e.g. officer, peshkar). */
  assignedRole: string;
  /** Display label for assigned desk (role · name). */
  officer: string;
  notes: string;
  status?: string;
  department?: string;
  description?: string;
  openedAt?: string;
  hearingDate?: string | null;
  hearingTime?: string | null;
}

const WF_OP: Record<WorkflowAction, string> = {
  Approve: 'approve',
  Correct: 'correct',
  Resend: 'resend',
  'Assign Department': 'assign_department',
  'Assign Officer': 'assign_officer',
  Escalate: 'escalate',
  'Update Status': 'start_review',
  Close: 'close',
  Reopen: 'reopen',
  'Submit Report': 'submit_report',
  'Need Hearing': 'need_hearing',
  'Mark Heard': 'mark_heard',
  Resolve: 'resolve',
  Reject: 'reject',
};

const ALLOWED_BY_STATUS: Record<string, WorkflowAction[]> = {
  submitted: ['Approve', 'Correct', 'Resend', 'Assign Department', 'Assign Officer', 'Reject'],
  pending_verification: ['Approve', 'Correct', 'Assign Department', 'Assign Officer', 'Reject'],
  under_review: ['Assign Department', 'Assign Officer', 'Escalate', 'Reject'],
  routed: ['Submit Report', 'Escalate', 'Reject'],
  investigation_completed: ['Need Hearing', 'Resolve', 'Close', 'Mark Heard', 'Reject'],
  awaiting_hearing: ['Close'],
  scheduled: ['Mark Heard', 'Close'],
  heard: ['Need Hearing', 'Resolve', 'Close'],
  resolved: ['Close', 'Reopen'],
  closed: ['Reopen'],
  rejected: ['Reopen'],
  reopened: ['Approve', 'Assign Department', 'Assign Officer', 'Reject'],
};

@Injectable({ providedIn: 'root' })
export class OpsService {
  private readonly api = inject(ApiClientService);
  private readonly complaints = inject(ComplaintService);

  private readonly pendingResolutions = signal<PendingResolution[]>([]);
  private readonly workflow = signal<WorkflowItem[]>([]);
  private readonly cases = signal<CaseRecord[]>([]);
  private readonly hearings = signal<HearingRecord[]>([]);
  private readonly roster = signal<RosterOfficer[]>([]);
  private readonly resolutions = signal<ResolutionRecord[]>([]);
  private readonly notifications = signal<AppNotification[]>([]);
  private readonly users = signal<AdminUser[]>([]);
  private readonly deptNames = signal<string[]>([
    'District Administration',
    'Jal Nigam',
    'Nagar Nigam',
    'PWD',
    'Electricity',
    'Revenue',
  ]);
  private readonly analyticsRaw = signal<{
    complaints?: { total: number; byStatus: Record<string, number>; byDepartment: Record<string, number> };
    cases?: { total: number; open: number };
    hearings?: { total: number; scheduled: number };
    roster?: { available: number };
  } | null>(null);

  readonly pendingResolutionList = computed(() => this.pendingResolutions());
  readonly workflowQueue = computed(() => this.workflow());
  readonly caseList = computed(() => this.cases());
  readonly hearingList = computed(() => this.hearings());
  readonly rosterList = computed(() => this.roster());
  readonly resolutionList = computed(() => this.resolutions());
  readonly notificationList = computed(() => this.notifications());
  readonly adminUsers = computed(() => this.users());

  /**
   * Real per-officer workload, derived from actual grievance assignments — the DM oversees
   * the desk and admin manages the system rather than carrying a caseload, so both are excluded.
   */
  readonly officerPerformance = computed(() => {
    const DONE_STATUSES = new Set<string>(['resolved', 'closed', 'rejected']);
    const comps = this.complaints.list();
    return this.users()
      .filter((u) => u.role !== 'dm' && u.role !== 'admin')
      .map((u) => {
        const assigned = comps.filter((c) => c.assignedOfficerRef != null && String(c.assignedOfficerRef) === String(u.id));
        const resolved = assigned.filter((c) => DONE_STATUSES.has(c.status)).length;
        return {
          id: u.id,
          officer: u.name,
          role: u.role,
          department: u.department || '—',
          load: assigned.length,
          pending: assigned.length - resolved,
          resolved,
        };
      })
      .sort((a, b) => b.load - a.load);
  });
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);

  get departments(): string[] {
    return this.deptNames();
  }

  readonly categories = ['Water Supply', 'Electricity', 'Sanitation', 'Roads', 'Certificates', 'Other'];

  readonly analytics = computed(() => {
    const raw = this.analyticsRaw();
    const cases = this.cases();
    const hearings = this.hearings();
    const byDeptMap = raw?.complaints?.byDepartment || {};
    const byDept = Object.entries(byDeptMap).map(([department, open]) => ({
      department,
      open: Number(open),
      resolved: 0,
    }));
    return {
      openCases: raw?.cases?.open ?? cases.filter((c) => c.status !== 'Closed').length,
      closedCases: cases.filter((c) => c.status === 'Closed').length,
      upcomingHearings: raw?.hearings?.scheduled ?? hearings.filter((h) => h.status === 'Scheduled').length,
      resolutions: this.resolutions().length,
      grantedRate: 0,
      byDept: byDept.length
        ? byDept
        : [{ department: '—', open: raw?.complaints?.total || 0, resolved: 0 }],
      officerLoad: this.roster().map((o) => ({ name: o.name, workload: o.workload })),
    };
  });

  async refresh(): Promise<void> {
    await this.complaints.refresh();
    const comps = this.complaints.list();

    this.workflow.set(
      comps.map((c) => ({
        id: c.id,
        trackingId: c.trackingId || c.id,
        subject: c.subject || c.category || c.complainantName,
        department: c.department || '—',
        stage: c.status,
        priority: c.priority,
        awaitingSince: toDisplayInstant(c.updatedAt) || c.updatedAt,
        allowed: ALLOWED_BY_STATUS[c.status] || ['Update Status', 'Escalate'],
      })),
    );


    const [casesRes, hearingsRes, rosterRes, notifRes, analyticsRes, usersRes, deptsRes] =
      await Promise.all([
        firstValueFrom(this.api.get<{ items: any[] }>('/cases')).catch(() => ({ items: [] })),
        firstValueFrom(this.api.get<{ items: any[] }>('/hearings')).catch(() => ({ items: [] })),
        firstValueFrom(this.api.get<{ items: any[] }>('/roster')).catch(() => ({ items: [] })),
        firstValueFrom(this.api.get<{ items: any[] }>('/notifications')).catch(() => ({ items: [] })),
        firstValueFrom(this.api.get<any>('/analytics/dashboard')).catch(() => null),
        firstValueFrom(this.api.get<{ items: any[] }>('/admin/users')).catch(() => ({ items: [] })),
        firstValueFrom(this.api.get<{ items: any[] }>('/admin/departments')).catch(() => ({ items: [] })),
      ]);

    this.cases.set(
      (casesRes.items || []).map((c) => {
        const complaint = this.complaints.list().find((x) => String(x.id) === String(c.id));
        const meta =
          complaint?.metadataJson && typeof complaint.metadataJson === 'object'
            ? (complaint.metadataJson as Record<string, unknown>)
            : {};
        return {
          id: c.id,
          caseNo: c.caseNumber || c.id,
          trackingId: c.complaintId || c.id,
          subject: c.title,
          officer: formatAssignedDeskLabel(
            c.assignedRole || complaint?.assignedRole,
            null,
            c.assignedOfficerRef,
          ),
          assignedOfficerRef: c.assignedOfficerRef ?? null,
          department: c.department || '—',
          status: ['closed', 'resolved'].includes(c.status) ? 'Closed' : 'Open',
          openedAt: toDisplayInstant(c.updatedAt) || new Date().toISOString(),
          notes:
            c.investigationReport ||
            (c.notes || []).map((n: any) => n.noteText || n.note).join('\n\n') ||
            '',
          investigationDueAt:
            String(complaint?.investigationDueAt || meta['investigationDueAt'] || '').trim() || null,
        };
      }),
    );

    this.users.set(
      (usersRes.items || []).map((u) => ({
        id: u.id,
        name: u.fullName || u.displayName || u.name,
        email: u.contactEmail || u.email || '',
        phone: u.contactPhone || u.phone || '',
        role: u.role || u.designation || '—',
        roles: u.roles?.length ? u.roles : u.role ? [u.role] : [],
        department: u.department,
        status: u.active === false || u.status === 'suspended' ? 'Suspended' : 'Active',
      })),
    );

    const caseNotesById = new Map(
      this.cases().map((c) => [String(c.id), c.notes] as const),
    );
    const userById = new Map(this.users().map((u) => [String(u.id), u] as const));

    this.pendingResolutions.set(
      this.complaints
        .list()
        .filter((c) =>
          [
            'routed',
            'under_review',
            'investigation_completed',
            'scheduled',
            'heard',
            'awaiting_hearing',
          ].includes(c.status),
        )
        .map((c) => {
          const meta =
            c.metadataJson && typeof c.metadataJson === 'object'
              ? (c.metadataJson as Record<string, unknown>)
              : {};
          const fromMeta = String(meta['investigationReport'] || meta['note'] || '').trim();
          const fromCase = caseNotesById.get(String(c.id)) || '';
          const holder = c.assignedOfficerRef != null ? userById.get(String(c.assignedOfficerRef)) : undefined;
          let role = String(c.assignedRole || meta['assignedRole'] || '').trim();
          if (!role || /^U-OFF-/i.test(role)) {
            role = String(holder?.role || '').trim();
          }
          const name = String(
            c.assignedOfficerName || meta['assignedOfficerName'] || holder?.name || '',
          ).trim();
          return {
            id: c.id,
            caseNo: c.id,
            trackingId: c.trackingId || c.id,
            subject: c.subject || c.category || String(c.id),
            assignedRole: role && !/^U-OFF-/i.test(role) ? role : '',
            officer: formatAssignedDeskLabel(role, name, c.assignedOfficerRef),
            notes: fromCase || fromMeta || c.description || '',
            status: c.status,
            department: c.department || '—',
            description: c.description || '',
            openedAt: toDisplayInstant(c.submittedAt || c.updatedAt) || undefined,
            hearingDate: c.hearingDate || null,
            hearingTime: c.hearingTime || null,
          };
        }),
    );

    this.resolutions.set(
      this.cases()
        .filter((c) => c.status === 'Closed')
        .map((c) => ({
          id: c.id,
          caseNo: c.caseNo,
          trackingId: c.trackingId,
          decision: c.notes || 'Closed',
          outcome: 'Granted' as const,
          decidedBy: c.officer,
          decidedAt: c.openedAt,
          followUps: '',
        })),
    );

    const complaintById = new Map(this.complaints.list().map((c) => [String(c.id), c] as const));

    this.hearings.set(
      (hearingsRes.items || []).map((h) => {
        const caseRef = h.grievanceRef || h.caseId || '—';
        const complaint = complaintById.get(String(caseRef));
        const rawDatePart = h.hearingDate || (h.scheduledAt || '').slice(0, 10) || '';
        // Never trust hearingDate blindly — a bad upstream row can carry a non-date string here,
        // and reconstructing "${datePart}T${timePart}" from it would hand DatePipe unparseable
        // garbage, which throws and aborts change detection for everything rendered after it.
        const datePart = /^\d{4}-\d{2}-\d{2}$/.test(rawDatePart) ? rawDatePart : '';
        const timePart = h.hearingTime || (h.scheduledAt || '').slice(11, 16) || '';
        const officerRef = h.officerRef || '—';
        const officerUser = officerRef !== '—' ? userById.get(String(officerRef)) : undefined;
        return {
          id: h.id,
          caseNo: caseRef,
          subject: complaint?.subject || complaint?.category || String(caseRef),
          scheduledAt:
            toDisplayInstant(h.scheduledAt) ||
            (datePart && timePart ? `${datePart}T${timePart}+05:30` : ''),
          hearingDate: datePart || undefined,
          hearingTime: timePart || undefined,
          venue: h.metadataJson?.venue || h.venue || '—',
          officers: officerRef,
          officerName: officerUser?.name || String(officerRef),
          status: mapHearingStatus(h.status, complaint?.status),
        } satisfies HearingRecord;
      }),
    );

    // users already set above (needed for assigned-role labels)

    this.roster.set(
      (rosterRes.items || []).map((r) => ({
        id: r.id ?? r.officerRef,
        officerRef: r.officerRef ?? r.id,
        name: r.officerName || String(r.officerRef || 'Officer'),
        role: r.role || 'officer',
        department: r.department || '—',
        availability:
          r.status === 'available'
            ? 'Available'
            : r.status === 'booked'
              ? 'In Hearing'
              : r.status === 'cancelled' || r.leaveUntil
                ? 'On Leave'
                : Number(r.workload) >= 3
                  ? 'Overloaded'
                  : 'Available',
        workload: Number(r.workload ?? (r.status === 'booked' ? 1 : 0)),
        leaveUntil: r.leaveUntil || undefined,
        currentAssignment: r.currentAssignment || r.metadataJson?.task || null,
      })),
    );

    this.notifications.set(
      (notifRes.items || []).map((n) => {
        const body = n.message || n.body || '';
        const type = (n.category || this.inferNotificationType(body)) as AppNotification['type'];
        return {
          id: n.id,
          title: roleNotificationTitle(type, n.targetRole || n.metadataJson?.targetRole),
          body,
          type,
          at: toDisplayInstant(n.createdAt || n.at) || n.createdAt || n.at,
          read: n.status === 'delivered',
          channel: n.channel,
          grievanceRef: n.grievanceRef,
          targetRole: n.targetRole || n.metadataJson?.targetRole || null,
          recipientRef: n.recipientRef ?? null,
          nextStep: n.nextStep || n.metadataJson?.nextStep || null,
          actionPath: n.actionPath || n.metadataJson?.actionPath || null,
          actionLabel: n.actionLabel || n.metadataJson?.actionLabel || null,
        };
      }),
    );

    if (deptsRes.items?.length) {
      this.deptNames.set(deptsRes.items.map((d: any) => d.name));
    }

    this.analyticsRaw.set(analyticsRes);
  }

  async applyWorkflow(
    id: string | number,
    action: WorkflowAction,
    extra: Record<string, string | number> = {},
  ): Promise<string> {
    const op = WF_OP[action];
    const body: Record<string, string | number> = {
      note: `${action} from Employee Portal`,
      ...extra,
    };
    if (action === 'Assign Department' && !body['department']) {
      body['department'] = 'Revenue';
    }
    await firstValueFrom(this.api.post(`/workflow/${id}/${op}`, body));
    await this.refresh();
    return `${action} recorded successfully.`;
  }

  async rescheduleHearing(id: string | number, when: string): Promise<string> {
    await firstValueFrom(this.api.patch(`/hearings/${id}`, { scheduledAt: when, status: 'Scheduled' }));
    await this.refresh();
    return 'Hearing rescheduled.';
  }

  async markNotificationRead(id: string | number): Promise<void> {
    const key = String(id);
    // Optimistic UI: clear unread badge + status immediately
    this.notifications.update((items) =>
      items.map((n) => (String(n.id) === key ? { ...n, read: true } : n)),
    );
    try {
      await firstValueFrom(this.api.post(`/notifications/${id}/read`, {}));
    } catch (err) {
      // Revert if API failed
      this.notifications.update((items) =>
        items.map((n) => (String(n.id) === key ? { ...n, read: false } : n)),
      );
      throw err;
    }
  }

  async markAllNotificationsRead(): Promise<void> {
    const snapshot = this.notifications();
    if (!snapshot.some((item) => !item.read)) return;
    this.notifications.update((items) => items.map((item) => ({ ...item, read: true })));
    try {
      await firstValueFrom(this.api.post('/notifications/read-all', {}));
    } catch (err) {
      this.notifications.set(snapshot);
      throw err;
    }
  }

  async listAssignableRoles(): Promise<
    Array<{
      id: string | number;
      roleName: string;
      description?: string;
      holderUserId?: string | number | null;
      holderName?: string | null;
      holderDepartment?: string | null;
    }>
  > {
    const res = await firstValueFrom(
      this.api.get<{ items: any[] }>('/admin/roles', { assignable: true }),
    );
    return res.items || [];
  }

  async listAllRoles(): Promise<
    Array<{
      id: string | number;
      roleName: string;
      description?: string;
      status?: string;
      holderUserId?: string | number | null;
      holderName?: string | null;
    }>
  > {
    const res = await firstValueFrom(this.api.get<{ items: any[] }>('/admin/roles'));
    return res.items || [];
  }

  async listRolesMatrix(): Promise<{
    items: Array<{
      id: string | number;
      roleName: string;
      displayName?: string;
      description?: string;
      status?: string;
      immutable?: boolean;
      assignedUserCount?: number;
      permissionCodes?: string[];
      permissionCount?: number;
      permissionTotal?: number;
      holderUserId?: string | number | null;
      holderName?: string | null;
      holderDepartment?: string | null;
    }>;
    referenceData: {
      resources: Array<{
        id: string | number;
        resourceName: string;
        description?: string;
        label?: string;
        sortOrder?: number;
        status?: string;
      }>;
      permissions: Array<{
        id: string | number;
        resourceRef: string | number;
        action: string;
        permissionCode: string;
        status?: string;
      }>;
      actions: string[];
    };
  }> {
    const res = await firstValueFrom(
      this.api.get<{
        items: any[];
        referenceData: {
          resources: any[];
          permissions: any[];
          actions: string[];
        };
      }>('/admin/roles', { matrix: true }),
    );
    return {
      items: res.items || [],
      referenceData: res.referenceData || { resources: [], permissions: [], actions: [] },
    };
  }

  async updateRolePermissions(
    roleId: string | number,
    permissionCodes: string[],
  ): Promise<{ permissionCodes: string[]; permissionCount: number }> {
    const res = await firstValueFrom(
      this.api.put<{ permissionCodes: string[]; permissionCount: number }>(
        `/admin/roles/${roleId}/permissions`,
        { permissionCodes },
      ),
    );
    return {
      permissionCodes: res.permissionCodes || [],
      permissionCount: res.permissionCount ?? (res.permissionCodes || []).length,
    };
  }

  async updateRoleMetadata(
    roleId: string | number,
    input: { description?: string; status?: string },
  ): Promise<{ description?: string; status?: string }> {
    return firstValueFrom(
      this.api.put<{ description?: string; status?: string }>(`/admin/roles/${roleId}`, input),
    );
  }

  async createDepartment(name: string): Promise<string> {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Department name is required');
    await firstValueFrom(this.api.post('/admin/departments', { name: trimmed }));
    this.deptNames.update((list) => (list.includes(trimmed) ? list : [...list, trimmed].sort()));
    return `Department “${trimmed}” created.`;
  }

  async createRole(input: { roleName: string; description?: string }): Promise<string> {
    const roleName = input.roleName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!roleName) throw new Error('Role name is required');
    await firstValueFrom(
      this.api.post('/admin/roles', {
        roleName,
        description: input.description?.trim() || undefined,
      }),
    );
    return `Role “${roleName}” created. Assign it to a desk profile in Settings, then use Assign on complaints.`;
  }

  async listResources(): Promise<
    Array<{
      id: string | number;
      resourceName: string;
      label?: string;
      description?: string;
      sortOrder?: number;
      status?: string;
      permissionCodes?: string[];
      permissionCount?: number;
      nonAdminGrantCount?: number;
      grantedRoleNames?: string[];
      canDelete?: boolean;
    }>
  > {
    const res = await firstValueFrom(this.api.get<{ items: any[] }>('/admin/resources'));
    return res.items || [];
  }

  async createResource(input: {
    resourceName: string;
    label?: string;
    description?: string;
  }): Promise<{
    id: string | number;
    resourceName: string;
    permissionCodes: string[];
  }> {
    return firstValueFrom(
      this.api.post<{
        id: string | number;
        resourceName: string;
        permissionCodes: string[];
      }>('/admin/resources', input),
    );
  }

  async updateResource(
    resourceId: string | number,
    input: {
      label?: string;
      description?: string;
      status?: string;
      sortOrder?: number;
    },
  ): Promise<any> {
    return firstValueFrom(this.api.put(`/admin/resources/${resourceId}`, input));
  }

  async deleteResource(resourceId: string | number): Promise<{ deleted: boolean }> {
    return firstValueFrom(this.api.delete<{ deleted: boolean }>(`/admin/resources/${resourceId}`));
  }

  async assignComplaintToRole(
    complaintId: string | number,
    role: string,
    note?: string,
    dueDays = 7,
  ): Promise<string> {
    await this.applyWorkflow(complaintId, 'Assign Officer', {
      role,
      note: note || `Assigned to role ${role}`,
      dueDays,
    });
    return `Complaint ${complaintId} assigned to role “${role}” (investigation due in ${dueDays} days).`;
  }

  async createDeskUser(input: {
    username: string;
    password: string;
    displayName: string;
    role: string;
    department: string;
    email?: string;
    phone?: string;
  }): Promise<string> {
    const username = input.username.trim();
    const displayName = (input.displayName || username).trim();
    if (!username) {
      throw new Error('Username is required');
    }
    if (!input.password) {
      throw new Error('Password is required');
    }
    const contactEmail = (input.email || '').trim().toLowerCase()
      || `${username.replace(/\s+/g, '.').toLowerCase()}@dist.gov.in`;
    const contactPhone = (input.phone || '').trim() || undefined;
    const existing = this.users().find(
      (u) =>
        u.email?.toLowerCase() === contactEmail ||
        u.name.toLowerCase() === displayName.toLowerCase(),
    );
    if (existing) {
      throw new Error(
        `User “${username}” already exists. Use Change role on that row instead of creating again.`,
      );
    }
    await firstValueFrom(
      this.api.post('/admin/users', {
        username,
        fullName: displayName,
        displayName,
        password: input.password,
        role: input.role,
        department: input.department,
        contactEmail,
        contactPhone: contactPhone || null,
        designation: input.role,
      }),
    );
    await this.refresh();
    return `Profile “${displayName}” created with role ${input.role}. Login: ${username}`;
  }

  async addUserRole(userId: string, role: string): Promise<string> {
    const current = this.users().find((u) => u.id === userId);
    if (current?.role === role) {
      throw new Error(`User already has role “${role}”.`);
    }
    await firstValueFrom(this.api.post(`/admin/users/${userId}/roles`, { role, transfer: true }));
    await this.refresh();
    return `Role “${role}” transferred to ${current?.name || userId}. Previous holder is now unassigned.`;
  }

  async updateDeskUser(
    userId: string | number,
    patch: {
      fullName?: string;
      department?: string;
      role?: string;
      password?: string;
      contactEmail?: string | null;
      contactPhone?: string | null;
    },
  ): Promise<string> {
    await firstValueFrom(this.api.patch(`/admin/users/${userId}`, patch));
    await this.refresh();
    return `Profile updated for ${patch.fullName || userId}.`;
  }

  async removeDeskUser(userId: string | number): Promise<string> {
    const current = this.users().find((u) => u.id === userId);
    await firstValueFrom(this.api.delete(`/admin/users/${userId}`));
    await this.refresh();
    return `Removed ${current?.name || userId}. Their desk role is now free.`;
  }

  async toggleUserStatus(id: string | number): Promise<void> {
    const current = this.users().find((u) => u.id === id);
    if (!current) return;
    const active = current.status !== 'Active';
    await firstValueFrom(this.api.patch(`/admin/users/${id}`, { active }));
    await this.refresh();
  }

  async listComplaintDocuments(complaintId: string | number): Promise<
    Array<{
      id: string;
      fileName: string;
      mimeType?: string;
      kind?: string;
      sizeBytes?: number;
      uploadedAt?: string;
      ocrDocumentId: string | null;
      ocrStatus: string;
      ocrResult: unknown;
    }>
  > {
    const res = await firstValueFrom(
      this.api.get<{ items: any[] }>('/documents', { complaintId: String(complaintId) }),
    );
    return (res.items || []).map((d) => ({
      id: d.id,
      fileName: d.fileName || d.id,
      mimeType: d.mimeType,
      kind: d.kind,
      sizeBytes: d.sizeBytes,
      uploadedAt: d.uploadedAt,
      ocrDocumentId: d.ocrDocumentId || null,
      ocrStatus: d.ocrStatus,
      ocrResult: d.ocrResult,
    }));
  }

  async uploadCaseAttachment(
    caseId: string | number,
    file: { fileName: string; mimeType: string; contentBase64: string },
  ): Promise<{ id: string; fileName: string; kind?: string }> {
    return firstValueFrom(
      this.api.post<{ id: string; fileName: string; kind?: string }>('/documents', {
        grievanceId: caseId,
        complaintId: caseId,
        fileName: file.fileName,
        mimeType: file.mimeType,
        contentBase64: file.contentBase64,
      }),
    );
  }

  async downloadCaseAttachment(documentId: string, fileName: string): Promise<void> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/${documentId}/file`));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  }

  async ingestOcr(
    documentId: string,
    payload: { ocrDocumentId: string; extracted?: Record<string, unknown>; rawText?: string },
  ): Promise<void> {
    await firstValueFrom(this.api.post(`/documents/${documentId}/ocr`, payload));
  }

  async saveResolution(id: string | number, notes: string, close: boolean): Promise<string> {
    await firstValueFrom(this.api.post(`/cases/${id}/notes`, { note: notes, noteType: 'resolution' }));
    if (close) {
      await this.applyWorkflow(id, 'Close', { note: notes });
      return 'Final order issued and case closed.';
    }
    await this.applyWorkflow(id, 'Resolve', { note: notes });
    return 'Final resolution recorded.';
  }

  async submitInvestigationReport(id: string | number, note: string): Promise<string> {
    await firstValueFrom(
      this.api.post(`/cases/${id}/notes`, { note, noteType: 'resolution' }),
    );
    return this.applyWorkflow(id, 'Submit Report', { note });
  }

  async requestHearing(id: string | number, note = ''): Promise<string> {
    await this.applyWorkflow(id, 'Need Hearing', { note: note || 'Hearing required' });
    return 'Hearing requested — Peshkar can schedule the next slot.';
  }

  async markHeard(id: string | number, note = '', hearingId?: string | number): Promise<string> {
    // Optimistic UI: flip hearing to Completed immediately so the button/badge update now.
    if (hearingId != null && hearingId !== '') {
      const key = String(hearingId);
      this.hearings.update((items) =>
        items.map((h) => (String(h.id) === key ? { ...h, status: 'Completed' as const } : h)),
      );
    }
    try {
      const extra: Record<string, string | number> = { note: note || 'Hearing conducted' };
      if (hearingId != null && hearingId !== '') {
        extra['hearingId'] = hearingId;
      }
      await this.applyWorkflow(id, 'Mark Heard', extra);
      await this.refresh();
      // Keep Completed if refresh still lagged for this hearing.
      if (hearingId != null && hearingId !== '') {
        const key = String(hearingId);
        this.hearings.update((items) =>
          items.map((h) =>
            String(h.id) === key && h.status === 'Scheduled'
              ? { ...h, status: 'Completed' as const }
              : h,
          ),
        );
      }
      return 'Hearing marked conducted.';
    } catch (err) {
      await this.refresh();
      throw err;
    }
  }

  /** Hearings for one case, oldest first (Hearing 1 → N). */
  hearingsForCase(caseNo: string | number | null | undefined): HearingRecord[] {
    if (caseNo == null || caseNo === '') return [];
    const key = String(caseNo);
    return [...this.hearings()]
      .filter((h) => String(h.caseNo) === key)
      .sort((a, b) => String(a.scheduledAt).localeCompare(String(b.scheduledAt)));
  }

  /** Sequence number (1-based) of a hearing within its case. */
  hearingSequence(hearing: HearingRecord | null | undefined): { index: number; total: number } {
    if (!hearing) return { index: 0, total: 0 };
    const siblings = this.hearingsForCase(hearing.caseNo);
    const index = siblings.findIndex((h) => String(h.id) === String(hearing.id));
    return { index: index >= 0 ? index + 1 : 0, total: siblings.length };
  }

  /** Grievances waiting for Peshkar to schedule (first or additional slot). */
  pendingHearingQueue(): Array<{ id: string | number; subject: string; status: string }> {
    return this.complaints
      .list()
      .filter((c) => c.status === 'awaiting_hearing' || c.status === 'scheduled')
      .map((c) => ({
        id: c.id,
        subject: c.subject || c.category || String(c.id),
        status: c.status,
      }));
  }

  /**
   * Dismissal is persisted per user, so a deleted row stays gone across refresh()
   * and the next sign-in. The list updates optimistically and rolls back if the
   * API rejects, matching markNotificationRead.
   */
  async deleteNotification(id: string | number): Promise<void> {
    const key = String(id);
    const snapshot = this.notifications();
    this.notifications.update((items) => items.filter((n) => String(n.id) !== key));
    try {
      await firstValueFrom(this.api.delete(`/notifications/${id}`));
    } catch (err) {
      this.notifications.set(snapshot);
      throw err;
    }
  }

  /**
   * With no ids, clears the caller's whole inbox; with ids, removes only those
   * (lets a filtered view, e.g. the DM inbox, clear just what it shows).
   */
  async deleteAllNotifications(ids?: Array<string | number>): Promise<void> {
    const snapshot = this.notifications();
    if (!ids) {
      this.notifications.set([]);
    } else {
      const keys = new Set(ids.map((id) => String(id)));
      this.notifications.update((items) => items.filter((n) => !keys.has(String(n.id))));
    }
    try {
      await firstValueFrom(this.api.post('/notifications/dismiss-all', ids ? { ids } : {}));
    } catch (err) {
      this.notifications.set(snapshot);
      throw err;
    }
  }

  private inferNotificationType(message: string): AppNotification['type'] {
    const text = (message || '').toLowerCase();
    if (/hearing|schedul/.test(text)) return 'Hearing';
    if (/resolv|closed|decision|outcome/.test(text)) return 'Resolution';
    if (/assign|routed|department|officer/.test(text)) return 'Assignment';
    if (/review|workflow|correct|approve|escalat/.test(text)) return 'Workflow';
    return 'System';
  }

  async addCaseNote(id: string | number, note: string): Promise<void> {
    await firstValueFrom(this.api.post(`/cases/${id}/notes`, { note }));
    await this.refresh();
  }

  async scheduleHearing(input: Omit<HearingRecord, 'id' | 'status'>): Promise<string> {
    await firstValueFrom(
      this.api.post('/hearings', {
        grievanceRef: input.caseNo,
        scheduledAt: input.scheduledAt,
        venue: input.venue,
        officerRef: input.officers,
      }),
    );
    await this.refresh();
    return 'Hearing scheduled successfully.';
  }

  async cancelHearing(id: string | number): Promise<string> {
    await firstValueFrom(this.api.post(`/hearings/${id}/cancel`, { note: 'Cancelled from portal' }));
    await this.refresh();
    return 'Hearing cancelled.';
  }

  async assignRosterTask(input: {
    officerRef: string | number;
    task: string;
    slotDate?: string;
  }): Promise<string> {
    const task = input.task.trim();
    if (!task) throw new Error('Enter a hearing / task description');
    const res = await firstValueFrom(
      this.api.post<{ message?: string; officerName?: string }>('/roster/assign', {
        officerRef: input.officerRef,
        task,
        slotDate: input.slotDate,
      }),
    );
    await this.refresh();
    return res.message || `Assigned to “${task}” and saved.`;
  }

  async clearRosterTask(officerRef: string | number): Promise<string> {
    await firstValueFrom(this.api.post('/roster/clear', { officerRef }));
    await this.refresh();
    return 'Active roster assignment cleared.';
  }

  async updateRosterStatus(
    rosterId: string | number,
    status: 'available' | 'booked' | 'cancelled',
  ): Promise<string> {
    await firstValueFrom(this.api.patch(`/roster/${rosterId}`, { status }));
    await this.refresh();
    return 'Roster availability updated.';
  }
}

/** Prefer desk role (+ holder name) over raw user ids like U-OFF-DM-0001. */
function mapHearingStatus(
  rawStatus: unknown,
  caseStatus?: string | null,
): HearingRecord['status'] {
  const s = String(rawStatus || '')
    .trim()
    .toLowerCase();
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
  if (s === 'held' || s === 'completed' || s === 'conducted') return 'Completed';
  if (s === 'rescheduled') return 'Rescheduled';

  // Heal UI when case already moved past hearing but hearing row lagged.
  const cs = String(caseStatus || '')
    .trim()
    .toLowerCase();
  if (['heard', 'resolved', 'closed'].includes(cs)) return 'Completed';

  return 'Scheduled';
}

function formatAssignedDeskLabel(
  role: unknown,
  name: unknown,
  officerRef?: unknown,
): string {
  const roleText = String(role || '').trim();
  const nameText = String(name || '').trim();
  const refText = String(officerRef || '').trim();
  const roleOk = roleText && !/^U-OFF-/i.test(roleText) && !/^U-\d/i.test(roleText);
  if (roleOk && nameText) return `${roleText} · ${nameText}`;
  if (roleOk) return roleText;
  if (nameText) return nameText;
  if (refText) return 'Unassigned role';
  return 'Unassigned';
}

function roleNotificationTitle(
  type: AppNotification['type'],
  targetRole?: string | null,
): string {
  const role = String(targetRole || '').toLowerCase();
  if (role === 'dm') {
    if (type === 'Workflow') return 'DM action needed';
    if (type === 'Hearing') return 'Hearing update';
    if (type === 'Resolution') return 'Resolution update';
  }
  if (role === 'peshkar') {
    if (type === 'Hearing') return 'Schedule hearing';
  }
  // officer + any custom investigative desk
  if (role === 'officer' || (role && !['admin', 'dm', 'peshkar', 'citizen'].includes(role))) {
    if (type === 'Assignment') return 'Case assigned to you';
    if (type === 'Hearing') return 'Hearing for your case';
    if (type === 'Resolution') return 'Case closed / resolved';
  }
  return type;
}
